'use client';

import { useEffect } from 'react';
import type { ReactNode } from 'react';

function useEscape(active: boolean, onClose?: () => void) {
  useEffect(() => {
    if (!active || !onClose) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [active, onClose]);
}

export function Modal({
  open,
  onClose,
  title,
  icon,
  children,
  actions,
  wide,
  labelledById = 'mx-modal-title',
}: {
  open: boolean;
  onClose?: () => void;
  title?: ReactNode;
  icon?: ReactNode;
  children?: ReactNode;
  actions?: ReactNode;
  wide?: boolean;
  labelledById?: string;
}) {
  useEscape(open, onClose);
  if (!open) return null;
  return (
    <div className="mx-overlay" role="presentation" onClick={onClose}>
      <div
        className={`mx-modal${wide ? ' mx-modal--wide' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title != null ? labelledById : undefined}
        onClick={(e) => e.stopPropagation()}
      >
        {icon}
        {title != null && (
          <h3 id={labelledById} className="mx-modal__title">
            {title}
          </h3>
        )}
        {children != null && <div className="mx-modal__body">{children}</div>}
        {actions != null && <div className="mx-modal__actions">{actions}</div>}
      </div>
    </div>
  );
}

export function Drawer({
  open,
  onClose,
  title,
  children,
  footer,
}: {
  open: boolean;
  onClose?: () => void;
  title?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
}) {
  useEscape(open, onClose);
  if (!open) return null;
  return (
    <div className="mx-overlay" role="presentation" onClick={onClose} style={{ justifyContent: 'flex-end' }}>
      <aside className="mx-drawer" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        {title != null && (
          <div className="mx-drawer__header">
            <span className="mx-h3">{title}</span>
            {onClose && (
              <button type="button" className="mx-btn mx-btn--ghost mx-btn--icon" aria-label="Tutup" onClick={onClose}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 6 6 18M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        )}
        <div className="mx-drawer__body">{children}</div>
        {footer != null && <div style={{ padding: 'var(--mx-sp-5)', borderTop: '1px solid var(--mx-border)' }}>{footer}</div>}
      </aside>
    </div>
  );
}
